import React, { useCallback, useEffect, useMemo, useState } from 'react'
import * as R from 'ramda'
import { useApi } from '~/store/store'
import { CheckIcon } from '~/assets/icons'
import { Box } from '@material-ui/core'
import { DocumentationTab } from './views'
import { mutualTheme } from '~/utils/theme'
import { makeStyles } from '@material-ui/styles'
import { trackEvent } from '~/service/analytics'
import { ServiceResponse } from '@mutual-libs/shared-types'
import { useAuthAccount } from '~/store/auth/useAuthAccount'
import { DocumentationOnboardContainer } from '../../components'
import { hasRequiredDocumentationAccountStatus } from '~/utils/analysisStatus'
import { DocumentsGroup, DocumentsGroupType } from '@mutual-services/account-documents-types'
import { Tabs, TabLabel, ErrorState, LoadingState, RedirectPJAnalysisStep } from '~/components'
import { translateTabNameToDocumentGroupType, getOnDocumentationTabClickAction } from '~/utils/analyticsHelpers'
import { fetchRequestedDocuments, translateDocumentType, FetchRequestDocumentsOutput, isTabCompleted, orderDocumentsGroupList } from '~/utils/documents'
import { useModal } from '~/hooks'

const useStyles = makeStyles({
  icon: {
    fontSize: 16,
    marginRight: mutualTheme.spacing(1),
    color: mutualTheme.palette.action.disabled
  },
  tabs: {
    marginTop: mutualTheme.spacing(1),
    marginBottom: mutualTheme.spacing(4)
  },
  button: {
    height: 56,
    fontWeight: 700,
    textTransform: 'none'
  },
  loadingContainer: {
    height: '100%',
    display: 'flex'
  }
})

const getTabLabels = (documentsGroup: ServiceResponse<DocumentsGroup[] | null>, documentsList: FetchRequestDocumentsOutput[]): TabLabel[] => {
  if (!documentsGroup) {
    return []
  }

  return documentsGroup.map(document => ({
    title: document.type === DocumentsGroupType.ADDITIONAL ? (document.typeName || translateDocumentType(document.type)) : translateDocumentType(document.type),
    icon: isTabCompleted(document, documentsList) ? <CheckIcon htmlColor={mutualTheme.palette.textGray2.main} /> : undefined
  }))
}

export const Documentation: React.FC = () => {
  const classes = useStyles()
  const { _id: accountId, pjAnalysisStatus } = useAuthAccount()
  const { status } = pjAnalysisStatus
  const [isOpen, toggleModal] = useModal()
  const [currentTab, setCurrentTab] = useState(0)
  const [tempCurrentTab, setTempCurrentTab] = useState({ useTempTab: false, tempTab: 0 })
  const [docData, setDocData] = useState<FetchRequestDocumentsOutput[]>([])
  const [getDocumentsGroupsByAccountId, getDocumentsGroupsByAccountIdState] = useApi('getDocumentsGroupsByAccountId')
  const { error, data: documents, loading, called } = getDocumentsGroupsByAccountIdState
  const shouldRedirect = useMemo(() => !hasRequiredDocumentationAccountStatus(status), [status])
  const [unsentDocs, setUnsentDocs] = useState<{ file: File }[] | null>([])

  const shouldShowDocumentUnsentModal = useMemo(() => unsentDocs ? unsentDocs.some(doc => doc.file !== null) : false, [unsentDocs])

  const updateDocData = useCallback((documents: FetchRequestDocumentsOutput[]) => {
    setDocData(docData.concat(documents))
  }, [docData, setDocData])

  const fetchDocuments = useCallback(async () => {
    if (shouldRedirect) {
      return
    }

    const result = await getDocumentsGroupsByAccountId({ accountId })

    if (result.ok) {
      const documentsInitialValues = fetchRequestedDocuments(result.data)
      updateDocData(documentsInitialValues)
    }
  }, [accountId, updateDocData, getDocumentsGroupsByAccountId, shouldRedirect])

  useEffect(() => {
    fetchDocuments()
  }, [])

  const orderedDocumentsGroupList = useMemo(() => orderDocumentsGroupList(documents), [documents])
  const labels = useMemo(() => getTabLabels(orderedDocumentsGroupList, docData), [orderedDocumentsGroupList, docData])
  const isButtonDisabled = useMemo(() => currentTab + 1 === labels.length, [labels, currentTab])

  const onGotoPage = useCallback((page: number) => {
    if (page < 0 || page > labels.length - 1) {
      return
    }

    setCurrentTab(page)
  }, [labels])

  const handleNextClick = useCallback(() => {
    if (currentTab + 1 === labels.length) {
      return
    }

    setCurrentTab(prevState => prevState + 1)
  }, [currentTab, labels, setCurrentTab])

  const resetTempTab = useCallback(() => {
    setTempCurrentTab({ useTempTab: false, tempTab: 0 })
  }, [setTempCurrentTab])

  const handleChange = useCallback((_: React.ChangeEvent<{}>, newValue: number) => {
    if (shouldShowDocumentUnsentModal) {
      setTempCurrentTab({ useTempTab: true, tempTab: newValue })
      toggleModal()
      return
    }

    const tabType = labels[newValue] && labels[newValue].title && translateTabNameToDocumentGroupType(labels[newValue].title)
    if (tabType) {
      trackEvent(getOnDocumentationTabClickAction(tabType))
    }

    setTempCurrentTab({ useTempTab: false, tempTab: 0 })
    setCurrentTab(newValue)
  }, [labels, setCurrentTab, shouldShowDocumentUnsentModal, toggleModal, setTempCurrentTab])

  if (shouldRedirect) {
    return (
      <RedirectPJAnalysisStep
        status={status}
      />
    )
  }

  if (error) {
    return (<ErrorState onRetry={() => getDocumentsGroupsByAccountId({ accountId })} />)
  }

  if (loading || !called || !orderedDocumentsGroupList || R.isEmpty(orderedDocumentsGroupList)) {
    return (<Box className={classes.loadingContainer}><LoadingState /></Box>)
  }

  return (
    <DocumentationOnboardContainer title='Documentos da empresa'>
      <Tabs
        labels={labels}
        value={currentTab}
        scrollButtons='auto'
        variant='scrollable'
        textColor='inherit'
        className={classes.tabs}
        onChange={handleChange as any}
      >
        {orderedDocumentsGroupList.map((document, index) => (
          <DocumentationTab
            tabIndex={index}
            docData={docData}
            document={document}
            onGotoPage={onGotoPage}
            updateDocData={updateDocData}
            documentsList={orderedDocumentsGroupList}
            disableButton={isButtonDisabled}
            handleNextClick={handleNextClick}
            key={`documentationTab-${index}`}
            setUnsentDocs={setUnsentDocs}
            toggleModal={toggleModal}
            isVisible={isOpen}
            tempCurrentTab={tempCurrentTab}
            resetTempTab={resetTempTab}
          />
        ))}
      </Tabs>
    </DocumentationOnboardContainer>
  )
}
